import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../../config';
import { CurrentTeamContext } from '../../contexts/CurrentTeamContext';
import { CurrentUserContext } from '../../contexts/CurrentUserContext';
import { RightSidebarContext } from '../../contexts/RightSidebarContext';
import Listing from '../../components/Listing';
import useFetch from '../../hooks/useFetch';
import { makeFirstCharUpper, parseProcessingStatus, subtractTime } from '../../helpers';

export default function OrderList() {
	let location = useLocation();

	let filterStatuses = [
		'draft',
		'pending',
		'accepted',
		'processing',
		'completed',
		'dispatched',
		'delivered',
		'disputed',
		'void',
		'rejected',
	];

	const options = {
		rightGroup: [
			{
				text: '12 months',
				name: '12months',
				onClick: () => {
					handleFilter(subtractTime({ months: 12 }), 'dateFrom');
					handleFilter(new Date(), 'dateTo');
				},
			},
			{
				text: '30 days',
				name: '30days',
				onClick: () => {
					handleFilter(subtractTime({ days: 30 }), 'dateFrom');
					handleFilter(new Date(), 'dateTo');
				},
			},
			{
				text: '1 week',
				name: '1week',
				onClick: () => {
					handleFilter(subtractTime({ weeks: 1 }), 'dateFrom');
					handleFilter(new Date(), 'dateTo');
				},
			},
			{
				text: '24 hours',
				name: '24hours',
				onClick: () => {
					handleFilter(subtractTime({ hours: 24 }), 'dateFrom');
					handleFilter(new Date(), 'dateTo');
				},
			},
		],
		leftGroup: [
			{
				text: 'All Trades',
				name: 'all',
				onClick: () => {
					handleFilter('all', 'teamType');
				},
			},
			{
				text: 'Purchases',
				name: '1',
				onClick: () => {
					handleFilter(1, 'teamType');
				},
			},
			{
				text: 'Sales',
				name: '2',
				onClick: () => {
					handleFilter(2, 'teamType');
				},
			},
		],
	};
	const sort = new URLSearchParams(location.search).get('sort');
	const assignment = new URLSearchParams(location.search).get('assignment');
	const presetStatuses = new URLSearchParams(location.search).get('statuses');
	const presetType = new URLSearchParams(location.search).get('type');
	const presetHold = new URLSearchParams(location.search).get('hold');
	const presetSearch = new URLSearchParams(location.search).get('search');
	const presetSkip = new URLSearchParams(location.search).get('skip');

	const [loading, setLoading] = useState(false);
	const [filter, setFilter] = useState({
		limit: 7,
		skip: Number(presetSkip) || 0,
		assignment: assignment || 'all',
		pages: 0,
		sort: sort || 'date.desc',
		statuses: presetStatuses || null,
		hold: presetHold || null,
		teamType: presetType ? presetType : 'all',
	});

	const [search, setSearch] = useState(presetSearch || null);
	const [pages, setPages] = useState(0);

	const [selectedTabs, setSelectedTabs] = useState({
		left: presetType ? presetType : 'all',
	});

	const topOfPage = useRef(null);

	const { rightSidebarDispatch } = useContext(RightSidebarContext);
	const { teamState } = useContext(CurrentTeamContext);

	const { data, fetch, reqLoading } = useFetch();

	const navigate = useNavigate();

	const statusKeyToNum = {
		draft: 0,
		pending: 1,
		accepted: 2,
		processing: 3,
		completed: 4,
		dispatched: 5,
		delivered: 6,
		disputed: 7,
		void: 8,
		rejected: 9,
	};

	function parseStatusesCsv(val) {
		if (!val) return [];
		return String(val)
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);
	}

	function setToCsv(set) {
		return Array.from(set)
			.sort((a, b) => Number(a) - Number(b))
			.join(',');
	}

	function toggleStatusCsv(csv, statusNum, enabled) {
		const set = new Set(parseStatusesCsv(csv));
		const key = String(statusNum);
		if (enabled) set.add(key);
		else set.delete(key);
		return set.size ? setToCsv(set) : null;
	}

	function syncFilterToUrl(filter, search) {
		const params = new URLSearchParams();

		if (filter?.sort) params.set('sort', filter.sort);
		if (filter?.statuses) params.set('statuses', filter.statuses);
		if (filter?.hold) params.set('hold', filter.hold);
		if (filter?.teamType && filter.teamType !== 'all') params.set('type', filter.teamType);
		if (filter?.assignment) params.set('assignment', filter.assignment);
		if (filter?.limit) params.set('limit', filter.limit);
		if (filter?.skip) params.set('skip', filter.skip);
		if (search) params.set('search', search);

		// You can add more fields as needed, e.g. dateFrom, dateTo, etc.
		const newUrl = `${location.pathname}?${params.toString()}`;
		navigate(newUrl, { replace: true }); // avoids pushing new history entries
	}

	useEffect(() => {
		syncFilterToUrl(filter, search);
	}, [filter, search]);

	function handlePagination(data) {
		let selected = data.selected;
		let newSkip = Math.ceil(selected * filter?.limit);
		setLoading(true);
		handleFilter(newSkip, 'skip');
	}

	function syncSidebarWithFilters(filter) {
		rightSidebarDispatch({
			type: 'SET_CONTENT_REFERENCE',
			payload: filter,
		});
	}

	function resetPagination() {
		let paginationBtns = document.getElementsByClassName('paginationPage');
		for (let i = 0; i < paginationBtns.length; i++) {
			paginationBtns[i]?.classList.remove('paginationActive');
		}
		paginationBtns[0]?.classList.add('paginationActive');
	}

	function handleFilter(value, name) {
		if (!value) {
			const resetFilter = {
				teamType: presetType ?? 'all',
				sort: 'date.desc',
				statuses: null,
				assignment: 'all',
				hold: null,
				limit: 7,
				skip: 0,
				pages: 0,
				currentPage: 1,
			};
			syncSidebarWithFilters(resetFilter);
			setFilter(resetFilter);
			return;
		}

		setFilter((prev) => {
			let next = {
				...prev,
				...(typeof value === 'object' ? value : { [name]: value }),
			};

			if (name !== 'skip') {
				next.skip = 0;
				next.currentPage = 1;
			}

			if (typeof value === 'object') {
				const changedKeys = Object.keys(value);

				for (const k of changedKeys) {
					if (k in statusKeyToNum) {
						next.statuses = toggleStatusCsv(next.statuses, statusKeyToNum[k], Boolean(value[k]));
					}
				}
			}

			syncSidebarWithFilters(next);
			return next;
		});
	}

	const handleFilterSelect = (value, name) => {
		setFilter((filter) => ({
			...filter,
			[name]: value?.value,
		}));
	};

	const updateDebounceSearch = useCallback(
		debounce((searchValue) => {
			setSearch(searchValue);
		}),
		[]
	);

	function checkPreset() {
		if (presetStatus) {
			return `${presetStatus}`;
		} else {
			return '';
		}
	}

	useEffect(() => {
		if (filter.skip === 0) {
			resetPagination();
		}
	}, [filter.skip]);

	function openFilter() {
		rightSidebarDispatch({ type: 'SET_TEXTBOXES_CLOSED' });
		rightSidebarDispatch({
			type: 'SET_CONTENT_REFERENCE',
			payload: { ...filter }, // pass full filter, not just status
		});

		rightSidebarDispatch({
			type: 'SET_CALLBACK',
			payload: (newFilter) => {
				handleFilter(newFilter);
				rightSidebarDispatch({ type: 'SET_SIDEBAR_OPEN', payload: false });
			},
		});

		rightSidebarDispatch({ type: 'SET_CONTENT_TYPE', payload: 3 });
		rightSidebarDispatch({ type: 'SET_SIDEBAR_OPEN', payload: true });
	}

	function checkStatuses(presetStatus, filter) {
		switch (presetStatus) {
			case '0':
				if (filter.draft !== undefined) {
					return true;
				}
				break;
			case '1':
				if (filter.pending !== undefined) {
					return true;
				}
				break;
			case '2':
				if (filter.accepted !== undefined) {
					return true;
				}
				break;
			case '4':
				if (filter.awaiting !== undefined) {
					return true;
				}
				break;
			case '6':
				if (filter.delivered !== undefined) {
					return true;
				}
				break;
			default:
				return false;
		}
	}

	async function buildURLAndFetch() {
		const url = new URL(`${config.apiv1}/order/orders.read/${teamState.currentTeam.teamUuid}`);
		assignment && url.searchParams.set('assignment', assignment);
		filter?.sort?.split('.')[0] === 'date' && url.searchParams.set('sortDate', filter?.sort?.split('.')[1]);
		filter?.sort?.split('.')[0] === 'team' && url.searchParams.set('sortAlphabet', filter?.sort?.split('.')[1]);
		filter?.assignment && url.searchParams.set('assignment', filter?.assignment);
		filter?.hold && url.searchParams.set('hold', filter?.hold);
		if (filter?.statuses) url.searchParams.set('statuses', filter.statuses);

		// LabManager
		filter?.reference && url.searchParams.set('reference', filter?.reference);
		filter?.practice && url.searchParams.set('practice', filter?.practice);
		filter?.dentist && url.searchParams.set('dentist', filter?.dentist);
		// Dentally
		filter?.patient_first_name && url.searchParams.set('patient_first_name', filter?.patient_first_name);
		filter?.patient_last_name && url.searchParams.set('patient_last_name', filter?.patient_last_name);
		filter?.patientId && url.searchParams.set('patientId', filter?.patientId);

		filter?.dateFrom && url.searchParams.set('dateFrom', filter?.dateFrom);
		filter?.dateTo && url.searchParams.set('dateTo', filter?.dateTo);
		filter?.buyer && url.searchParams.set('buyer', filter?.buyer);
		filter?.teamType && filter?.teamType !== 'all' && url.searchParams.set('teamType', filter?.teamType);

		search && url.searchParams.set('search', search);
		filter?.limit && url.searchParams.set('limit', filter?.limit);
		filter?.skip && url.searchParams.set('skip', filter?.skip);

		let res = await fetch(url.href, null, true);

		if (res?.ok) {
			setPages(res?.data && res.data.length > 0 ? Math.ceil(res?.totalDocuments / filter?.limit) : 0);
		} else {
			setPages(1);
		}
		setLoading(false);
	}

	function debounce(cb, delay = 400) {
		let timeout;
		return (...args) => {
			setLoading(true);
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				cb(...args);
			}, delay);
		};
	}

	useEffect(() => {
		buildURLAndFetch();
	}, [teamState.reload, filter, search, location]);

	return (
		<>
			<div ref={topOfPage}></div>
			{/* {teamState?.userPermissions['manageOrders'] ? ( */}
			<Listing
				openFilter={openFilter}
				handlePagination={handlePagination}
				handleSearch={updateDebounceSearch}
				loading={reqLoading || loading}
				handleFilterSelect={handleFilterSelect}
				handleFilter={handleFilter}
				setSelectedTabs={setSelectedTabs}
				selectedTabs={selectedTabs}
				options={options}
				listItems={data}
				filter={filter}
				pages={pages}
				uri={`/orders${filter?.teamType ? `?type=${filter.teamType}` : ''}`}
				type='orders'
			/>
			{/* ) : (
				<>
					<h4>You do not have permission to see or manage orders.</h4>
					<h4 className='marginTop-20'>Contact your administrator for more information.</h4>
				</>
			)} */}
		</>
	);
}
